import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  GoogleMap,
  Marker,
  InfoWindow,
  useJsApiLoader,
} from "@react-google-maps/api";
import { Card, CardBody, CardTitle, Row, Col, Spinner, FormGroup, Label, Input } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import axios from "axios";
import { toast } from "react-toastify";
import moment from "moment";

const containerStyle = {
  width: "100%",
  height: "600px",
};

const libraries = ["places"];

const defaultCenter = {
  lat: 26.3351, // Default center (Libya coordinates)
  lng: 17.2283,
};

const RealTimeDrivers = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapZoom, setMapZoom] = useState(6); // Zoom out pour voir toute la Libye
  const mapRef = useRef(null);
  const boundsRef = useRef(null);
  const userHasInteractedRef = useRef(false); // Pour savoir si l'utilisateur a interagi avec la carte
  const isInitialLoadRef = useRef(true); // Pour le premier chargement
  
  // Filtres de statut
  const [showOnTrip, setShowOnTrip] = useState(true);
  const [showOnline, setShowOnline] = useState(true);
  const [showDelayed, setShowDelayed] = useState(true);
  const [showOffline, setShowOffline] = useState(true);

  const { isLoaded, loadError } = useJsApiLoader({
    id: "real-time-drivers-map",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY,
    libraries,
    version: "weekly",
  });

  const fetchDrivers = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_WEB_APP_URL}/api/web/drivers-location`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success && response.data.data) {
        const driversData = response.data.data;
        setDrivers(driversData);

        // Calculate bounds to fit all drivers - seulement au premier chargement
        if (driversData.length > 0 && isInitialLoadRef.current) {
          const validDrivers = driversData.filter(
            (d) => d.latitude && d.longitude
          );
          if (validDrivers.length > 0) {
            // Calculate bounds
            const lats = validDrivers.map((d) => d.latitude);
            const lngs = validDrivers.map((d) => d.longitude);
            const minLat = Math.min(...lats);
            const maxLat = Math.max(...lats);
            const minLng = Math.min(...lngs);
            const maxLng = Math.max(...lngs);

            // Calculate center
            const avgLat = (minLat + maxLat) / 2;
            const avgLng = (minLng + maxLng) / 2;
            setMapCenter({ lat: avgLat, lng: avgLng });

            // Create bounds
            if (window.google && window.google.maps) {
              boundsRef.current = new window.google.maps.LatLngBounds();
              validDrivers.forEach((driver) => {
                boundsRef.current.extend(
                  new window.google.maps.LatLng(
                    driver.latitude,
                    driver.longitude
                  )
                );
              });

              // Fit bounds to show all drivers - seulement au premier chargement
              if (mapRef.current && boundsRef.current) {
                const padding = 50;
                mapRef.current.fitBounds(boundsRef.current, {
                  top: padding,
                  right: padding,
                  bottom: padding,
                  left: padding,
                });
              }
            } else {
              // Fallback: calculate zoom based on spread
              const latDiff = maxLat - minLat;
              const lngDiff = maxLng - minLng;
              const maxDiff = Math.max(latDiff, lngDiff);
              
              // Adjust zoom based on spread (larger spread = lower zoom)
              let calculatedZoom = 15;
              if (maxDiff > 0.1) calculatedZoom = 10;
              else if (maxDiff > 0.05) calculatedZoom = 11;
              else if (maxDiff > 0.02) calculatedZoom = 12;
              else if (maxDiff > 0.01) calculatedZoom = 13;
              else if (maxDiff > 0.005) calculatedZoom = 14;
              
              setMapZoom(calculatedZoom);
            }
            isInitialLoadRef.current = false;
          }
        }
      }
    } catch (error) {
      console.error("Error fetching drivers:", error);
      toast.error(
        error?.response?.data?.message ||
          "Error fetching drivers location data"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDrivers();
    // Refresh drivers every 10 seconds
    const interval = setInterval(() => {
      fetchDrivers();
    }, 10000);

    return () => clearInterval(interval);
  }, [fetchDrivers]);

  const handleMarkerClick = (driver) => {
    setSelectedDriver(driver);
  };

  const handleInfoWindowClose = () => {
    setSelectedDriver(null);
  };

  // Handlers pour détecter les interactions utilisateur
  const handleMapZoomChanged = useCallback(() => {
    if (mapRef.current) {
      try {
        const zoom = mapRef.current.getZoom();
        const center = mapRef.current.getCenter();
        if (typeof zoom === 'number' && !isNaN(zoom) && center) {
          const lat = center.lat();
          const lng = center.lng();
          if (typeof lat === 'number' && !isNaN(lat) && typeof lng === 'number' && !isNaN(lng)) {
            setMapZoom(zoom);
            setMapCenter({ lat, lng });
            userHasInteractedRef.current = true;
          }
        }
      } catch (error) {
        console.error('Error in handleMapZoomChanged:', error);
      }
    }
  }, []);

  const handleMapDragEnd = useCallback(() => {
    if (mapRef.current) {
      try {
        const center = mapRef.current.getCenter();
        const zoom = mapRef.current.getZoom();
        if (center && zoom) {
          const lat = center.lat();
          const lng = center.lng();
          if (typeof lat === 'number' && !isNaN(lat) && typeof lng === 'number' && !isNaN(lng) && typeof zoom === 'number' && !isNaN(zoom)) {
            setMapCenter({ lat, lng });
            setMapZoom(zoom);
            userHasInteractedRef.current = true;
          }
        }
      } catch (error) {
        console.error('Error in handleMapDragEnd:', error);
      }
    }
  }, []);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    
    // Fit bounds seulement au premier chargement
    if (drivers.length > 0 && boundsRef.current && isInitialLoadRef.current) {
      const padding = 50;
      map.fitBounds(boundsRef.current, {
        top: padding,
        right: padding,
        bottom: padding,
        left: padding,
      });
      isInitialLoadRef.current = false;
    }
  }, [drivers]);

  // Mettre à jour les bounds sans forcer le fitBounds (pour les rafraîchissements)
  useEffect(() => {
    if (isLoaded && drivers.length > 0 && mapRef.current) {
      const validDrivers = drivers.filter(
        (d) => d.latitude && d.longitude
      );
      
      if (validDrivers.length > 0 && window.google && window.google.maps) {
        // Mettre à jour les bounds mais ne pas forcer fitBounds si l'utilisateur a interagi
        boundsRef.current = new window.google.maps.LatLngBounds();
        validDrivers.forEach((driver) => {
          boundsRef.current.extend(
            new window.google.maps.LatLng(
              driver.latitude,
              driver.longitude
            )
          );
        });

        // Ne faire fitBounds que si c'est le premier chargement et que l'utilisateur n'a pas interagi
        if (isInitialLoadRef.current && !userHasInteractedRef.current) {
          const padding = 50;
          mapRef.current.fitBounds(boundsRef.current, {
            top: padding,
            right: padding,
            bottom: padding,
            left: padding,
          });
          isInitialLoadRef.current = false;
        }
      }
    }
  }, [isLoaded, drivers]);

  document.title = "Real-Time Drivers | Moeen";

  if (loadError) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs
            maintitle="Moeen"
            title="Real-Time Drivers"
            breadcrumbItem="Drivers Map"
          />
          <div className="alert alert-danger">
            Error loading Google Maps: {loadError.message}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs
          maintitle="Moeen"
          title="Real-Time Drivers"
          breadcrumbItem="Drivers Map"
        />

        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <CardTitle className="h4 mb-3">
                  Real-Time Drivers Location
                </CardTitle>
                
                {/* Filtres de statut */}
                <Row className="mb-3">
                  <Col md={12}>
                    <Card>
                      <CardBody>
                        <h6 className="mb-3">Filter by Status:</h6>
                        <Row>
                          <Col md={3}>
                            <FormGroup check>
                              <Label check>
                                <Input
                                  type="checkbox"
                                  checked={showOnTrip}
                                  onChange={(e) => setShowOnTrip(e.target.checked)}
                                />{" "}
                                <span
                                  style={{
                                    display: "inline-block",
                                    width: "12px",
                                    height: "12px",
                                    backgroundColor: "#001e60",
                                    borderRadius: "50%",
                                    marginRight: "5px",
                                  }}
                                ></span>
                                On Trip
                              </Label>
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup check>
                              <Label check>
                                <Input
                                  type="checkbox"
                                  checked={showOnline}
                                  onChange={(e) => setShowOnline(e.target.checked)}
                                />{" "}
                                <span
                                  style={{
                                    display: "inline-block",
                                    width: "12px",
                                    height: "12px",
                                    backgroundColor: "green",
                                    borderRadius: "50%",
                                    marginRight: "5px",
                                  }}
                                ></span>
                                Online
                              </Label>
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup check>
                              <Label check>
                                <Input
                                  type="checkbox"
                                  checked={showDelayed}
                                  onChange={(e) => setShowDelayed(e.target.checked)}
                                />{" "}
                                <span
                                  style={{
                                    display: "inline-block",
                                    width: "12px",
                                    height: "12px",
                                    backgroundColor: "orange",
                                    borderRadius: "50%",
                                    marginRight: "5px",
                                  }}
                                ></span>
                                Delayed
                              </Label>
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup check>
                              <Label check>
                                <Input
                                  type="checkbox"
                                  checked={showOffline}
                                  onChange={(e) => setShowOffline(e.target.checked)}
                                />{" "}
                                <span
                                  style={{
                                    display: "inline-block",
                                    width: "12px",
                                    height: "12px",
                                    backgroundColor: "red",
                                    borderRadius: "50%",
                                    marginRight: "5px",
                                  }}
                                ></span>
                                Offline
                              </Label>
                            </FormGroup>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
                {loading && !isLoaded ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                    <p className="mt-2">Loading map...</p>
                  </div>
                ) : !isLoaded ? (
                  <div className="text-center py-5">
                    <p>Loading Google Maps...</p>
                  </div>
                ) : (
                  <div
                    style={{
                      position: "relative",
                      borderRadius: "8px",
                      overflow: "hidden",
                    }}
                  >
                    <GoogleMap
                      mapContainerStyle={containerStyle}
                      center={mapCenter && typeof mapCenter.lat === 'number' && !isNaN(mapCenter.lat) && typeof mapCenter.lng === 'number' && !isNaN(mapCenter.lng) ? mapCenter : defaultCenter}
                      zoom={typeof mapZoom === 'number' && !isNaN(mapZoom) ? mapZoom : 6}
                      onLoad={onMapLoad}
                      onZoomChanged={handleMapZoomChanged}
                      onDragEnd={handleMapDragEnd}
                      options={{
                        draggable: true,
                        clickableIcons: true,
                        gestureHandling: 'greedy',
                        mapTypeControl: true,
                        streetViewControl: true,
                        fullscreenControl: true,
                        zoomControl: true,
                      }}
                    >
                      {drivers
                        .filter((driver) => {
                          if (!driver.latitude || !driver.longitude) return false;
                          
                          // Filtrer selon les statuts sélectionnés
                          if (driver.is_on_trip && !showOnTrip) return false;
                          if (!driver.is_on_trip) {
                            if (driver.gps_state === "ONLINE" && !showOnline) return false;
                            if (driver.gps_state === "DELAYED" && !showDelayed) return false;
                            if (driver.gps_state === "OFFLINE" && !showOffline) return false;
                          }
                          
                          return true;
                        })
                        .map((driver) => {
                          // Déterminer l'icône basée sur is_on_trip ou gps_state
                          let iconUrl;
                          if (driver.is_on_trip) {
                            // Driver en trip : utiliser le même marqueur que "online" mais en bleu
                            iconUrl = "http://maps.google.com/mapfiles/ms/icons/blue-dot.png";
                          } else if (driver.gps_state === "ONLINE") {
                            iconUrl = "http://maps.google.com/mapfiles/ms/icons/green-dot.png";
                          } else if (driver.gps_state === "DELAYED") {
                            iconUrl = "http://maps.google.com/mapfiles/ms/icons/yellow-dot.png";
                          } else {
                            iconUrl = "http://maps.google.com/mapfiles/ms/icons/red-dot.png";
                          }

                          return (
                            <Marker
                              key={driver.id}
                              position={{
                                lat: driver.latitude,
                                lng: driver.longitude,
                              }}
                              onClick={() => handleMarkerClick(driver)}
                              icon={{
                                url: iconUrl,
                                scaledSize: new window.google.maps.Size(32, 32),
                              }}
                            />
                          );
                        })}

                      {selectedDriver && (
                        <InfoWindow
                          position={{
                            lat: selectedDriver.latitude,
                            lng: selectedDriver.longitude,
                          }}
                          onCloseClick={handleInfoWindowClose}
                        >
                          <div style={{ padding: "5px" }}>
                            <h6 style={{ margin: "0 0 5px 0", fontWeight: "bold" }}>
                              {selectedDriver.name || "Driver"}
                            </h6>
                            <p style={{ margin: "2px 0", fontSize: "12px" }}>
                              <strong>Email:</strong> {selectedDriver.email || "N/A"}
                            </p>
                            <p style={{ margin: "2px 0", fontSize: "12px" }}>
                              <strong>Phone:</strong>{" "}
                              {selectedDriver.phone_code && selectedDriver.mobno
                                ? `${selectedDriver.phone_code} ${selectedDriver.mobno}`
                                : selectedDriver.mobno || "N/A"}
                            </p>
                            <p style={{ margin: "2px 0", fontSize: "12px" }}>
                              <strong>GPS Status:</strong>{" "}
                              <span
                                style={{
                                  color:
                                    selectedDriver.gps_state === "ONLINE"
                                      ? "green"
                                      : selectedDriver.gps_state === "DELAYED"
                                      ? "orange"
                                      : "red",
                                  fontWeight: "bold",
                                }}
                              >
                                {selectedDriver.gps_state || "OFFLINE"}
                              </span>
                            </p>
                            <p style={{ margin: "2px 0", fontSize: "12px" }}>
                              <strong>Last GPS Update:</strong>{" "}
                              {selectedDriver.last_gps_at
                                ? moment(selectedDriver.last_gps_at).format("YYYY-MM-DD HH:mm:ss")
                                : "N/A"}
                            </p>
                            {selectedDriver.is_on_trip && (
                              <>
                                <p style={{ margin: "2px 0", fontSize: "12px" }}>
                                  <strong>Booking ID:</strong>{" "}
                                  <span style={{ color: "#001e60", fontWeight: "bold" }}>
                                    {selectedDriver.booking_id || "N/A"}
                                  </span>
                                </p>
                                <p style={{ margin: "2px 0", fontSize: "12px" }}>
                                  <strong>Status:</strong>{" "}
                                  <span style={{ color: "#001e60", fontWeight: "bold" }}>
                                    {selectedDriver.status_name || "N/A"}
                                  </span>
                                </p>
                              </>
                            )}
                            <p style={{ margin: "2px 0", fontSize: "12px" }}>
                              <strong>Location:</strong>{" "}
                              {selectedDriver.latitude.toFixed(6)},{" "}
                              {selectedDriver.longitude.toFixed(6)}
                            </p>
                          </div>
                        </InfoWindow>
                      )}
                    </GoogleMap>
                  </div>
                )}

                {!loading && drivers.length === 0 && (
                  <div className="text-center py-5">
                    <p className="text-muted">
                      No drivers with location data found.
                    </p>
                  </div>
                )}

                {!loading && drivers.length > 0 && (
                  <div className="mt-3">
                    <p className="text-muted mb-0">
                      <small>
                        Showing {drivers.filter((driver) => {
                          if (!driver.latitude || !driver.longitude) return false;
                          if (driver.is_on_trip && !showOnTrip) return false;
                          if (!driver.is_on_trip) {
                            if (driver.gps_state === "ONLINE" && !showOnline) return false;
                            if (driver.gps_state === "DELAYED" && !showDelayed) return false;
                            if (driver.gps_state === "OFFLINE" && !showOffline) return false;
                          }
                          return true;
                        }).length} of {drivers.length} driver(s) on map. Map updates
                        every 10 seconds.
                      </small>
                    </p>
                    <p className="text-muted mb-0">
                      <small>
                        <span
                          style={{
                            display: "inline-block",
                            width: "12px",
                            height: "12px",
                            backgroundColor: "#001e60",
                            borderRadius: "50%",
                            marginRight: "5px",
                          }}
                        ></span>
                        On Trip
                        <span
                          style={{
                            display: "inline-block",
                            width: "12px",
                            height: "12px",
                            backgroundColor: "green",
                            borderRadius: "50%",
                            marginLeft: "10px",
                            marginRight: "5px",
                          }}
                        ></span>
                        Online
                        <span
                          style={{
                            display: "inline-block",
                            width: "12px",
                            height: "12px",
                            backgroundColor: "orange",
                            borderRadius: "50%",
                            marginLeft: "10px",
                            marginRight: "5px",
                          }}
                        ></span>
                        Delayed
                        <span
                          style={{
                            display: "inline-block",
                            width: "12px",
                            height: "12px",
                            backgroundColor: "red",
                            borderRadius: "50%",
                            marginLeft: "10px",
                            marginRight: "5px",
                          }}
                        ></span>
                        Offline
                      </small>
                    </p>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default RealTimeDrivers;

